"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import type { UserDTO } from "@app-layer/dto/UserDTO";
import type { MyProjectViewDTO } from "@app-layer/use-cases/GetMyRoleInProject";
import NotificationBell from "./NotificationBell";

const ROLE_LABEL_PROJECT: Record<string, string> = {
  LEADER: "負責人",
  MEMBER: "協作員",
  VIEWER: "觀察員",
};

const I = {
  dashboard: <svg className="icon" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>,
  list: <svg className="icon" viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1.5"/><circle cx="4" cy="12" r="1.5"/><circle cx="4" cy="18" r="1.5"/></svg>,
  plus: <svg className="icon" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  chart: <svg className="icon" viewBox="0 0 24 24"><polyline points="3 17 9 11 13 15 21 7"/><polyline points="14 7 21 7 21 14"/></svg>,
  briefcase: <svg className="icon" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
  hand: <svg className="icon" viewBox="0 0 24 24"><path d="M18 11V6a2 2 0 0 0-4 0v5"/><path d="M14 10V4a2 2 0 0 0-4 0v6"/><path d="M10 10.5V6a2 2 0 0 0-4 0v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.9-5.7-2.3l-6-7c-.7-1-.5-2.4.5-3.2.9-.7 2.2-.6 3 .3L6 12"/></svg>,
  lock: <svg className="icon" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  users: <svg className="icon" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  clock: <svg className="icon" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  wrench: <svg className="icon" viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  box: <svg className="icon" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  logout: <svg className="icon" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
};

const ROLE_LABEL: Record<string, { label: string; color: string }> = {
  ADMIN:              { label: "最高管理員", color: "#f59e0b" },
  DIRECTOR:           { label: "專案總監",   color: "#DC2626" },
  PROJECT_MANAGER:    { label: "專案管理",   color: "#3b82f6" },
  STAFF:              { label: "員工",       color: "#94a3b8" },
};

/**
 * 目前專案名 · 可摺疊
 *   長標題預設只顯示首段（第一個空格 / · / ／ / / 前），旁邊有「+」按鈕
 *   點 + → 展開下方看到剩餘副標；再點「-」→ 收回
 *   短標題（沒可切點）→ 直接整段顯示、不出按鈕
 */
function splitBrand(name: string): { brand: string; sub: string } {
  const m = name.match(/^([^\s·／/]+)([\s·／/].+)?$/);
  if (!m) return { brand: name, sub: "" };
  return { brand: m[1], sub: (m[2] ?? "").trim() };
}
function ProjectNameExpandable({ name }: { name: string }) {
  const [open, setOpen] = useState(false);
  const { brand, sub } = splitBrand(name);
  return (
    <div className={`project-ctx-name${open ? " open" : ""}`}>
      <div className="brand-row">
        <span className="brand" title={name}>{brand}</span>
        {sub && (
          <button
            type="button"
            className="toggle"
            aria-label={open ? "收合" : "展開專案完整名稱"}
            aria-expanded={open}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {open ? "−" : "+"}
          </button>
        )}
      </div>
      {sub && open && <div className="rest">{sub}</div>}
    </div>
  );
}

function useProjectContext(): MyProjectViewDTO | null {
  const pathname = usePathname();
  const match = pathname?.match(/^\/projects\/(\d+)/);
  const projectId = match ? match[1] : null;
  const [ctx, setCtx] = useState<MyProjectViewDTO | null>(null);
  useEffect(() => {
    if (!projectId) {
      setCtx(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/my/projects/${projectId}/tasks`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled) setCtx(j);
      })
      .catch(() => {
        if (!cancelled) setCtx(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  return ctx;
}

// 🆕 2026-10-13 C 方案 · 多案極簡列(當前案高亮 + 子選單)
interface MyProjectItem {
  projectId: number;
  projectName: string;
  projectStatus: string;
  myRole: string | null;
}
const STATUS_DOT: Record<string, { color: string; title: string }> = {
  planning:                { color: "#94a3b8", title: "規劃中" },
  pending_leader_fill:     { color: "#F59E0B", title: "待負責人填料" },
  pending_pm_review:       { color: "#3B82F6", title: "等 PM 審" },
  pending_director_close:  { color: "#7C3AED", title: "等總監審" },
  pending_finance:         { color: "#10B981", title: "等財務審" },
  completed:               { color: "#10B981", title: "已完成" },
  archived:                { color: "#64748B", title: "已封存" },
  active:                  { color: "#3B82F6", title: "進行中" },
  paused:                  { color: "#94a3b8", title: "暫停" },
  rejected:                { color: "#DC2626", title: "已駁回" },
};
function MyProjectsListC({ activeProjectId }: { activeProjectId: number | null; activeMyRole: string | null }) {
  const [items, setItems] = useState<MyProjectItem[] | null>(null);
  useEffect(() => {
    fetch("/api/my/projects", { credentials: "include", cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const list = (j?.projects ?? []) as MyProjectItem[];
        // in-progress 優先排前面,completed/archived 排後面
        const prio = (s: string) => (s === "completed" || s === "archived" ? 1 : 0);
        list.sort((a, b) => prio(a.projectStatus) - prio(b.projectStatus) || a.projectName.localeCompare(b.projectName, "zh-Hant"));
        setItems(list);
      })
      .catch(() => setItems([]));
  }, []);
  if (!items || items.length === 0) return null;
  return (
    <div className="project-context">
      <div className="group" style={{ color: "#fbbf24" }}>● 目前專案</div>
      {/* 🆕 2026-10-13 V1:單行 · 標題左 · 角色靠右淡色 · 不帶狀態 dot / 子選單 */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((it) => {
          const isActive = it.projectId === activeProjectId;
          const roleLabel = it.myRole ? (ROLE_LABEL_PROJECT[it.myRole] ?? it.myRole) : null;
          return (
            <Link
              key={it.projectId}
              href={`/projects/${it.projectId}`}
              title={`${it.projectName}${roleLabel ? ` · ${roleLabel}` : ""}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "6px 10px", borderRadius: 6,
                borderLeft: isActive ? "3px solid #F59E0B" : "3px solid transparent",
                background: isActive ? "#334155" : "transparent",
                color: isActive ? "#fff" : "#CBD5E1",
                fontSize: 12, fontWeight: isActive ? 500 : 400,
                textDecoration: "none",
                gap: 8,
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                {it.projectName}
              </span>
              {roleLabel && (
                <span style={{ fontSize: 10, color: "#94A3B8", flexShrink: 0 }}>{roleLabel}</span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
const subLinkStyle: React.CSSProperties = {
  display: "block",
  padding: "3px 10px",
  fontSize: 11,
  color: "#94A3B8",
  textDecoration: "none",
  borderRadius: 4,
};

const DEPT_LIST = [
  { slug: "operations",  label: "營運部" },
  { slug: "marketing",   label: "行銷部" },
  { slug: "liaison",     label: "窗口部" },
  { slug: "management",  label: "管理部" },
  { slug: "sales",       label: "市場開發" },
  { slug: "finance",     label: "財務部" },
  { slug: "hr",          label: "人資部" },
  { slug: "it",          label: "資訊部" },
  { slug: "engineering", label: "工程部" },
  { slug: "planning",    label: "企劃部" },
  { slug: "legal",       label: "法務部" },
] as const;

function DeptGroup({ initiallyOpen }: { initiallyOpen: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  useEffect(() => { if (initiallyOpen) setOpen(true); }, [initiallyOpen]);
  return (
    <>
      <div className="group">部門</div>
      <button
        type="button"
        className="dept-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <svg className="icon" viewBox="0 0 24 24">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          各部門
        </span>
        <span style={{ fontSize: 11, opacity: 0.6 }}>{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="dept-sublist">
          {DEPT_LIST.map((d) => (
            <Link key={d.slug} href={`/dept/${d.slug}`}>
              {d.label}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export default function Sidebar({ user }: { user: UserDTO | null }) {
  const pathname = usePathname();
  const projectContext = useProjectContext();
  // ─── mobile drawer state ───
  const [drawerOpen, setDrawerOpen] = useState(false);
  // 路徑變化自動關 drawer（點 link 跳頁後不用手動關）
  useEffect(() => { setDrawerOpen(false); }, [pathname]);
  // Esc 關閉
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  if (!user) {
    return (
      <aside className="sidebar">
        <div className="brand">
          <span className="dot" />
          給臣專案管理
        </div>
        <nav>
          <Link href="/login" className={pathname === "/login" || pathname?.startsWith("/login/") ? "active" : ""}>{I.lock}登入</Link>
        </nav>
      </aside>
    );
  }

  const isAdmin = user.role === "ADMIN";
  const isStaff = user.role === "STAFF";
  // 「能管專案」= 擁有 project.list（看全部專案）或 project.gantt
  // ADMIN 的 system master 會讓 hasFeature 永遠回 true
  const perms = user.permissionDetails ?? null;

  // 2026-10-06 · H5 fix: legacy user permissionDetails=NULL 時 Sidebar 不要全空
  //   新進員工 / Line LIFF 自動建的帳號可能 permission_details=NULL
  //   fallback 到 role 預設（避免只剩「我的工作區」）
  //   有 permissionDetails（含 features: {}）就不走 fallback，尊重 ADMIN 的清空指令
  const isLegacyNoPerms = perms === null;
  const hasFeature = (slug: string): boolean => {
    if (perms?.system) return true;
    if (perms?.features?.[slug]) return true;
    if (isLegacyNoPerms) {
      // Role-based fallback（只給保守預設 · 不給管理類 slug）
      if (user.role === "ADMIN" || user.role === "DIRECTOR") return true;
      if (user.role === "PROJECT_MANAGER") return !slug.startsWith("system.");
      // STAFF / CONSULTANT 保守：只個人工作區 + 看公開報價 + 掃發票
      return slug.startsWith("personal.")
        || slug === "quote.view"
        || slug === "finance.scan"
        || slug === "finance.my"
        || slug === "dept.view_own"
        || slug === "store.view";
    }
    return false;
  };
  const canManageProjects = hasFeature("project.list") || hasFeature("project.gantt");
  // 🆕 2026-10-12 Phase 3 C-3:DIRECTOR 不給建案(只負責審核)
  //   財務部成員也不給建案(只負責財務審)
  const canCreateProject = hasFeature("project.create") && user.role !== "DIRECTOR";
  const canSeeMediaLog = hasFeature("media.log") || hasFeature("media.log.own") || hasFeature("media.log.admin");
  const canSeeMediaQuot = hasFeature("media.quotation");
  const canSeeHR = hasFeature("hr.employee");
  const canSeeFinanceAdmin = hasFeature("finance.approve") || hasFeature("finance.budget");
  const canSeeSystem = !!perms?.system || hasFeature("system.account") || hasFeature("system.employee");
  // 「能看績效」= ADMIN / PROJECT_MANAGER
  const canViewPerformance = canManageProjects;
  // Defensive: 萬一 JWT 帶到舊角色字串，仍能渲染不爆
  const roleMeta = ROLE_LABEL[user.role] ?? { label: user.role, color: "#94a3b8" };

  const inProjectContext = !!(projectContext && projectContext.myRole);

  return (
    <>
      {/* ─── 手機頂欄 · < 768px 顯示 · Hamburger + brand + Bell ─── */}
      <div className="mobile-topbar">
        <button type="button" className="hamburger" onClick={() => setDrawerOpen(true)} aria-label="打開選單">
          <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <div className="mobile-brand">
          <span className="dot" /> 給臣專案管理
        </div>
        <button type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("open-search"))}
          aria-label="搜尋"
          style={{ background: "transparent", border: "0.5px solid var(--border-strong)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1, marginRight: 6 }}>
          🔍
        </button>
        <NotificationBell />
      </div>

      {/* ─── Drawer overlay · 只手機模式 ─── */}
      {drawerOpen && (
        <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
      )}

      <aside className={`sidebar${drawerOpen ? " drawer-open" : ""}`}>
      <div className="brand">
        給臣專案管理<span className="dot" />
        <button type="button" className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="關閉選單">×</button>
      </div>

      <div className="user-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div className="name">{user.displayName}</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("open-search"))}
              aria-label="搜尋 (⌘K)"
              title="搜尋 (Cmd/Ctrl+K)"
              style={{ background: "transparent", border: "0.5px solid var(--border-strong)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>
              🔍
            </button>
            <NotificationBell />
          </div>
        </div>
        {user.role !== "STAFF" && (
          <div className="meta">
            <span className="role-tag" style={{ background: roleMeta.color }}>
              {roleMeta.label}
            </span>
          </div>
        )}
      </div>

      <nav>
        <div className="group" data-num="01">個人</div>
        <Link href="/my" className={pathname === "/my" ? "active" : ""}>{I.dashboard}我的工作區</Link>
        <Link href="/my/todos" className={pathname === "/my/todos" ? "active" : ""}>📋 我的待辦</Link>
        {!isStaff && <Link href="/" className={pathname === "/" ? "active" : ""}>{I.chart}儀表板</Link>}

        {/* 🆕 2026-10-13 C 方案:極簡列 · 多案一覽 · 當前案高亮 · 展開子選單 */}
        <MyProjectsListC
          activeProjectId={projectContext?.project.id ?? null}
          activeMyRole={projectContext?.myRole ?? null}
        />


        {canManageProjects && (
          <>
            <div className="group" data-num="02">專案</div>
            <Link href="/projects" className={pathname === "/projects" || pathname?.startsWith("/projects/") ? "active" : ""}>{I.list}所有專案</Link>
            <Link href="/projects/subs" className={pathname === "/projects/subs" || pathname?.startsWith("/projects/subs/") ? "active" : ""}>{I.list}部門專案總覽</Link>
            {canCreateProject && (
              <Link href="/projects/new" className={pathname === "/projects/new" || pathname?.startsWith("/projects/new/") ? "active" : ""}>{I.plus}新增專案</Link>
            )}
          </>
        )}

        {canSeeMediaLog && (
          <>
            <div className="group" data-num="02b">媒體</div>
            <Link href="/media-log" className={pathname?.startsWith("/media-log") ? "active" : ""}>
              <svg className="icon" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="9" cy="9" r="2"/>
                <path d="M21 15l-5-5-4 5-3-3-5 5"/>
              </svg>
              媒體媒合日誌
            </Link>
            {canSeeMediaQuot && (
              <Link href="/media-quotations" className={pathname?.startsWith("/media-quotations") ? "active" : ""}>
                {I.briefcase}媒體媒合報價單
              </Link>
            )}
          </>
        )}

        {canSeeHR && (
          <>
            <div className="group" data-num="03">人事</div>
            <Link href="/hr/employees" className={pathname === "/hr/employees" || pathname?.startsWith("/hr/employees/") ? "active" : ""}>{I.users}員工資料表</Link>
          </>
        )}

        <div className="group" data-num="04">財務</div>
        <Link href="/finance/scan" className={pathname === "/finance/scan" || pathname?.startsWith("/finance/scan/") ? "active" : ""}>{I.plus}掃發票報帳</Link>
        <Link href="/finance/my" className={pathname === "/finance/my" || pathname?.startsWith("/finance/my/") ? "active" : ""}>{I.list}我的請款</Link>
        <Link href="/finance/quotations" className={pathname === "/finance/quotations" || pathname?.startsWith("/finance/quotations/") ? "active" : ""}>{I.briefcase}專案請款單</Link>
        {canSeeFinanceAdmin && (
          <>
            <Link href="/finance" className={pathname === "/finance" || pathname?.startsWith("/finance/") ? "active" : ""}>{I.box}財務系統</Link>
            {hasFeature("finance.approve") && (
              <Link href="/finance/claims" className={pathname === "/finance/claims" || pathname?.startsWith("/finance/claims/") ? "active" : ""}>{I.list}請款審核</Link>
            )}
            {hasFeature("finance.budget") && (
              <Link href="/finance/budgets" className={pathname === "/finance/budgets" || pathname?.startsWith("/finance/budgets/") ? "active" : ""}>{I.chart}預算設定</Link>
            )}
          </>
        )}

        {canSeeSystem && (
          <>
            <div className="group" data-num="05">系統</div>
            <Link href="/admin" className={pathname === "/admin" || pathname?.startsWith("/admin/") ? "active" : ""}>{I.dashboard}系統管理首頁</Link>
            {hasFeature("system.account") && (
              <Link href="/admin/users" className={pathname === "/admin/users" || pathname?.startsWith("/admin/users/") ? "active" : ""}>{I.users}帳號管理</Link>
            )}
            {hasFeature("hr.attendance_qr") && (
              <Link href="/admin/employees" className={pathname === "/admin/employees" || pathname?.startsWith("/admin/employees/") ? "active" : ""}>{I.users}員工打卡 QR</Link>
            )}
          </>
        )}

        <div className="group" />
        <form action="/api/auth/logout" method="POST">
          <button type="submit" className="logout-btn">
            {I.logout}登出
          </button>
        </form>
      </nav>
    </aside>
    </>
  );
}
